/**
 * Taking an order off automatic dispatch, and handing it back.
 *
 * The override is what makes the automation acceptable. A dispatcher who knows the regular
 * customer at number 14 will not take the new driver needs to act on that, and a system that
 * makes them fight it gets switched off entirely rather than argued with. Pressed means a
 * person has it.
 *
 * ## Where it lives, which has now moved twice
 *
 * It began on the queue row, and the reason recorded at the time was that the alternative
 * was "three clicks into a panel". It is one: the row opens the order, and the button is in
 * the order's own header beside its reference. What that move bought is the queue fitting
 * the width the desk actually has — the row was carrying five columns and a button through a
 * space that fits four, and paying for it with a horizontal scrollbar that never went away.
 *
 * The thing the original decision was protecting still holds: taking an order over is two
 * presses from seeing it, not a trip through a settings screen.
 */

import { useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon } from '../ui';
import { dispatchApi } from './api';

export function TakeOver({
  orderId,
  automatic,
  onChanged,
}: {
  orderId: string;
  /** Whether the automation still has it. */
  automatic: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);

  async function toggle(event: MouseEvent) {
    // Whatever this sits inside may itself be a control — the queue row was one. Taking an
    // order off automation is a different intent from opening it.
    event.stopPropagation();

    setBusy(true);

    try {
      await dispatchApi.setAutomatic(orderId, !automatic);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      size="sm"
      icon
      variant={automatic ? 'quiet' : 'outline'}
      aria-pressed={!automatic}
      title={automatic ? t('queue.takeOver') : t('queue.handBack')}
      aria-label={automatic ? t('queue.takeOver') : t('queue.handBack')}
      disabled={busy}
      onClick={(e) => void toggle(e)}
    >
      <Icon name="user" small />
    </Button>
  );
}
